import { Typography, Button, Container, Box, Collapse, Chip } from "@mui/material";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useTranslation } from "react-i18next";
import { fadeInUp } from "../constants/animations";

const HeroSection = ({ expanded, onToggleExpanded }) => {
  const { t } = useTranslation();

  const { scrollY } = useScroll();
  const yHero = useTransform(scrollY, [0, 500], [0, 150]);
  const opacityHero = useTransform(scrollY, [0, 300], [1, 0]);
  const scaleHero = useTransform(scrollY, [0, 300], [1, 1.1]);

  const subtitleWords = t("home.hero.subtitle").split(" ");

  return (
    <Box
      component={motion.div}
      style={{ y: yHero }}
      sx={{
        position: "relative",
        minHeight: { xs: "85vh", md: "85vh" },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        overflow: "hidden",
      }}
    >
      {/* Background image: starts zoomed in slightly and eases down to its
          resting scale on mount, independent of the scroll-linked motion
          applied to the content below (Ken Burns-style cinematic entrance). */}
      <Box
        component={motion.div}
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2, ease: "easeOut" }}
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: "url('/images/wde.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(135deg, rgba(8,18,38,0.88) 0%, rgba(21,101,192,0.72) 60%, rgba(180,70,0,0.45) 100%)",
          zIndex: 1,
        }}
      />

      <Container
        component={motion.div}
        style={{ opacity: opacityHero, scale: scaleHero }}
        sx={{ position: "relative", zIndex: 2, px: { xs: 3, md: 6 } }}
      >
        <motion.div initial="hidden" animate="visible" variants={fadeInUp}>

          {/* Availability badge */}
          <motion.div
            animate={{ scale: [1, 1.04, 1] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            style={{ display: "inline-block", marginBottom: "16px" }}
          >
            <Chip
              label={t("home.hero.availableBadge")}
              size="small"
              sx={{
                bgcolor: "rgba(255,255,255,0.15)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.3)",
                fontWeight: 600,
                letterSpacing: "0.03em",
                backdropFilter: "blur(8px)",
                "& .MuiChip-label": { px: 1.5 },
              }}
            />
          </motion.div>

          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontSize: { xs: "2.2rem", md: "3.5rem" },
              fontWeight: 800,
              color: "#ffffff",
              letterSpacing: "-0.02em",
              textShadow: "0 2px 20px rgba(0,0,0,0.25)",
            }}
          >
            {t("home.hero.welcome")}
          </Typography>

          {/* Word-by-word animated subtitle */}
          <motion.p
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.04, delayChildren: 0.3 } },
            }}
            initial="hidden"
            animate="visible"
            style={{
              fontWeight: 500,
              fontSize: "clamp(1.1rem, 2vw, 1.4rem)",
              color: "rgba(255,255,255,0.88)",
              letterSpacing: "0.01em",
              lineHeight: 1.6,
              maxWidth: "720px",
              margin: "0 auto",
              padding: 0,
            }}
          >
            {subtitleWords.map((word, i) => (
              <motion.span
                key={i}
                variants={{
                  hidden: { opacity: 0, y: 12 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
                }}
                style={{ display: "inline-block", marginRight: "0.3em" }}
              >
                {word}
              </motion.span>
            ))}
          </motion.p>

          <Typography
            variant="body1"
            sx={{
              mt: 2,
              fontWeight: 500,
              color: "rgba(255,255,255,0.78)",
              maxWidth: "620px",
              margin: "16px auto 0",
            }}
          >
            {t("home.hero.valueProp")}
          </Typography>

          <Box sx={{ mt: 4 }}>
            <Button
              variant="outlined"
              onClick={onToggleExpanded}
              sx={{
                mt: 2,
                mr: 2,
                color: "#fff",
                borderColor: "rgba(255,255,255,0.7)",
                borderWidth: 2,
                px: 3,
                fontWeight: 600,
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(255,255,255,0.12)",
                },
              }}
            >
              {expanded ? t("home.hero.showLess") : t("home.hero.learnMore")}
            </Button>
            <Link to="/contact" style={{ textDecoration: "none" }}>
              <Button
                variant="contained"
                sx={{
                  mt: 2,
                  px: 4,
                  fontWeight: 700,
                  backgroundColor: "#FF6D00",
                  color: "#fff",
                  boxShadow: "0 4px 20px rgba(255,109,0,0.4)",
                  "&:hover": {
                    backgroundColor: "#FF8F00",
                    boxShadow: "0 6px 28px rgba(255,109,0,0.5)",
                  },
                }}
              >
                {t("home.hero.getStarted")}
              </Button>
            </Link>
          </Box>

          {/* Collapsible Content */}
          <Collapse in={expanded}>
            <Box sx={{ mt: 2 }}>
              <Container maxWidth="md" sx={{ py: 4 }}>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    mb: 1,
                    color: "rgba(255,255,255,0.95)",
                    textAlign: "center",
                    fontSize: { xs: "1.2rem", md: "1.5rem" },
                  }}
                >
                  {t("home.about.title")}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    lineHeight: 1.8,
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    textAlign: "justify",
                    color: "rgba(255,255,255,0.82)",
                  }}
                >
                  {t("home.about.description1")}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    lineHeight: 1.8,
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    textAlign: "justify",
                    color: "rgba(255,255,255,0.82)",
                  }}
                >
                  {t("home.about.description2")}
                </Typography>
                <Typography
                  variant="h5"
                  component="h6"
                  sx={{
                    fontWeight: "bold",
                    mb: 1,
                    color: "#FF9E40",
                    textAlign: "center",
                    fontSize: { xs: "1.2rem", md: "1.5rem" },
                  }}
                >
                  {t("home.about.callToAction")}
                </Typography>
              </Container>
            </Box>
          </Collapse>
        </motion.div>
      </Container>
    </Box>
  );
};

export default HeroSection;
